import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { AuthContext } from "../../../context/Auth/AuthContext";
import { ROLES } from "../../../constants/roles";
import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import { i18n } from "../../../translate/i18n";
import MasterPageBreadcrumb from "../../../layout/master/MasterPageBreadcrumb";
import { getMasterRouteMeta } from "../../../layout/master/masterRouteMeta";
import MasterSpinner from "../../../components/Master/MasterSpinner";
import MasterModal from "../../../components/Master/MasterModal";
import MasterConfirmationModal from "../../../components/Master/MasterConfirmationModal";
import AuthField from "../../../components/Auth/AuthField";
import "../Plans/plans-page.css";

const emptyForm = {
	name: "",
	quantity: "",
	price: "",
	active: true,
};

const ADDON_COLORS = ["#6366f1", "#8b5cf6", "#06b6d4", "#f59e0b"];

function IconPlus() {
	return (
		<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<line x1="12" y1="5" x2="12" y2="19" />
			<line x1="5" y1="12" x2="19" y2="12" />
		</svg>
	);
}

function IconEdit() {
	return (
		<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
			<path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
		</svg>
	);
}

function IconTrash() {
	return (
		<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="3 6 5 6 21 6" />
			<path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
		</svg>
	);
}

function formatPrice(value) {
	if (value === null || value === undefined || value === "") return "—";
	const n = Number(value);
	if (Number.isNaN(n)) return "—";
	return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatQuantity(value) {
	return Number(value || 0).toLocaleString("pt-BR");
}

function AddonCard({ addon, color, deleting, onEdit, onDelete }) {
	const [hovered, setHovered] = useState(false);

	return (
		<article
			className="mp-card"
			style={{
				borderColor: hovered ? `${color}55` : undefined,
				boxShadow: hovered ? `0 8px 32px ${color}22` : undefined,
			}}
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
		>
			<div
				className="mp-card__accent"
				style={{ background: `linear-gradient(90deg, ${color}, ${color}55)` }}
			/>

			<div className="mp-card__header">
				<div className="min-w-0">
					<div className="mp-card__name-row">
						<h3 className="mp-card__name">{addon.name}</h3>
					</div>
					<div className="mp-card__meta">
						<span>#{addon.id}</span>
						<span className="mp-card__meta-sep">·</span>
						<span>{i18n.t("master.addons.typeTokens")}</span>
					</div>
				</div>
				<span
					className={`mp-card__badge ${
						addon.active ? "mp-card__badge--active" : "mp-card__badge--inactive"
					}`}
				>
					{addon.active
						? i18n.t("master.pages.statusActive")
						: i18n.t("master.pages.statusInactive")}
				</span>
			</div>

			<div
				className="mp-card__featured"
				style={{
					background: `linear-gradient(135deg, ${color}18 0%, ${color}08 100%)`,
					border: `1px solid ${color}25`,
				}}
			>
				<div className="mp-card__featured-label" style={{ color }}>
					{i18n.t("master.addons.fieldQuantity")}
				</div>
				<div className="mp-card__featured-price">{formatQuantity(addon.quantity)}</div>
				<div className="mp-card__featured-hint">{i18n.t("master.addons.tokensUnit")}</div>
			</div>

			<div className="mp-card__prices">
				<div className="mp-card__price-row">
					<span className="mp-card__price-label">{i18n.t("master.addons.fieldPrice")}</span>
					<div className="mp-card__price-right">
						<span className="mp-card__price-value">{formatPrice(addon.price)}</span>
					</div>
				</div>
			</div>

			<div className="mp-card__actions">
				<button
					type="button"
					className="mp-card__btn-edit"
					onClick={() => onEdit(addon)}
					onMouseEnter={(e) => {
						e.currentTarget.style.background = `${color}22`;
						e.currentTarget.style.borderColor = `${color}66`;
					}}
					onMouseLeave={(e) => {
						e.currentTarget.style.background = "";
						e.currentTarget.style.borderColor = "";
					}}
				>
					<IconEdit />
					{i18n.t("master.addons.edit")}
				</button>
				<button
					type="button"
					className="mp-card__btn-delete"
					disabled={deleting}
					title={i18n.t("master.addons.delete")}
					aria-label={i18n.t("master.addons.delete")}
					onClick={() => onDelete(addon)}
				>
					<IconTrash />
				</button>
			</div>
		</article>
	);
}

export default function MasterAddons() {
	const history = useHistory();
	const { user, loading } = useContext(AuthContext);
	const routeMeta = getMasterRouteMeta("/admin-master/addons");
	const [addons, setAddons] = useState([]);
	const [loadingAddons, setLoadingAddons] = useState(true);
	const [modalOpen, setModalOpen] = useState(false);
	const [editing, setEditing] = useState(null);
	const [form, setForm] = useState(emptyForm);
	const [saving, setSaving] = useState(false);
	const [deletingId, setDeletingId] = useState(null);
	const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
	const [addonToDelete, setAddonToDelete] = useState(null);

	const fetchAddons = useCallback(() => {
		setLoadingAddons(true);
		return api
			.get("/master/addons")
			.then(({ data }) => setAddons(data || []))
			.catch((err) => toastError(err))
			.finally(() => setLoadingAddons(false));
	}, []);

	useEffect(() => {
		if (loading) return;
		if (!user || user.profile !== ROLES.master) {
			history.replace("/");
			return;
		}
		fetchAddons();
	}, [user, loading, history, fetchAddons]);

	const sortedAddons = useMemo(
		() =>
			[...addons].sort((a, b) =>
				String(a.name || "").localeCompare(String(b.name || ""))
			),
		[addons]
	);

	const activeCount = useMemo(
		() => addons.filter((a) => a.active).length,
		[addons]
	);
	const inactiveCount = addons.length - activeCount;

	const openCreate = () => {
		setEditing(null);
		setForm(emptyForm);
		setModalOpen(true);
	};

	const openEdit = (addon) => {
		setEditing(addon);
		setForm({
			name: addon.name || "",
			quantity: String(addon.quantity ?? ""),
			price: String(addon.price ?? ""),
			active: Boolean(addon.active),
		});
		setModalOpen(true);
	};

	const closeModal = () => {
		if (saving) return;
		setModalOpen(false);
		setEditing(null);
		setForm(emptyForm);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		const payload = {
			name: form.name.trim(),
			quantity: Number(form.quantity),
			price: Number(form.price),
			active: Boolean(form.active),
		};
		setSaving(true);
		try {
			if (editing) {
				await api.put(`/master/addons/${editing.id}`, payload);
				toast.success(i18n.t("master.addons.toastUpdated"));
			} else {
				await api.post("/master/addons", payload);
				toast.success(i18n.t("master.addons.toastCreated"));
			}
			setModalOpen(false);
			setEditing(null);
			setForm(emptyForm);
			await fetchAddons();
		} catch (err) {
			toastError(err);
		} finally {
			setSaving(false);
		}
	};

	const handleOpenDeleteConfirm = (addon) => {
		setAddonToDelete(addon);
		setConfirmDeleteOpen(true);
	};

	const handleConfirmDelete = async () => {
		if (!addonToDelete) return;
		setDeletingId(addonToDelete.id);
		try {
			const { data } = await api.delete(`/master/addons/${addonToDelete.id}`);
			if (data?.deleted) {
				toast.success(i18n.t("master.addons.toastDeleted"));
			} else {
				toast.success(i18n.t("master.addons.toastDisabled"));
			}
			setAddonToDelete(null);
			await fetchAddons();
		} catch (err) {
			toastError(err);
		} finally {
			setDeletingId(null);
		}
	};

	if (loading || !user || user.profile !== ROLES.master) {
		return null;
	}

	const modalFooter = (
		<>
			<button type="button" className="btn-secondary" onClick={closeModal} disabled={saving}>
				{i18n.t("master.addons.cancel")}
			</button>
			<button
				type="submit"
				form="master-addon-form"
				className="btn-primary"
				disabled={saving}
			>
				{saving ? i18n.t("master.addons.saving") : i18n.t("master.addons.save")}
			</button>
		</>
	);

	const stats = [
		{ labelKey: "master.addons.statTotal", value: addons.length, color: "#6366f1" },
		{ labelKey: "master.addons.statActive", value: activeCount, color: "#10b981" },
		{ labelKey: "master.addons.statInactive", value: inactiveCount, color: "#6b7280" },
	];

	return (
		<div className="master-plans-page mx-auto w-full max-w-none box-border">
			<MasterPageBreadcrumb
				title={i18n.t(routeMeta.titleKey)}
				breadcrumbs={routeMeta.breadcrumbs}
			/>

			<div className="master-plans-page__top">
				<div>
					<h1 className="master-plans-page__title">{i18n.t("master.addons.pageTitle")}</h1>
					<p className="master-plans-page__subtitle">
						{i18n.t("master.addons.pageSubtitle")}
					</p>
				</div>
				<button type="button" className="master-plans-page__create" onClick={openCreate}>
					<IconPlus />
					{i18n.t("master.addons.create")}
				</button>
			</div>

			<div className="master-plans-page__stats">
				{stats.map((stat) => (
					<div key={stat.labelKey} className="master-plans-page__stat">
						<span
							className="master-plans-page__stat-dot"
							style={{
								background: stat.color,
								boxShadow: `0 0 6px ${stat.color}`,
							}}
						/>
						<span className="master-plans-page__stat-label">{i18n.t(stat.labelKey)}</span>
						<span className="master-plans-page__stat-value">{stat.value}</span>
					</div>
				))}
			</div>

			<div className="master-plans-page__section">
				<span className="master-plans-page__section-title">
					{i18n.t("master.addons.listTitle")}
				</span>
				<span className="master-plans-page__section-count">
					{addons.length} {i18n.t("master.addons.countLabel")}
				</span>
			</div>

			{loadingAddons ? (
				<div className="master-plans-page__loading">
					<MasterSpinner />
					<span>{i18n.t("master.addons.loading")}</span>
				</div>
			) : sortedAddons.length === 0 ? (
				<div className="master-plans-page__empty">
					<p className="master-plans-page__empty-title">{i18n.t("master.addons.empty")}</p>
					<p className="m-0 text-sm">{i18n.t("master.addons.emptyHint")}</p>
					<button type="button" className="master-plans-page__create mt-2" onClick={openCreate}>
						<IconPlus />
						{i18n.t("master.addons.create")}
					</button>
				</div>
			) : (
				<div className="master-plans-page__grid">
					{sortedAddons.map((addon, index) => (
						<AddonCard
							key={addon.id}
							addon={addon}
							color={ADDON_COLORS[index % ADDON_COLORS.length]}
							deleting={deletingId === addon.id}
							onEdit={openEdit}
							onDelete={handleOpenDeleteConfirm}
						/>
					))}
				</div>
			)}

			<MasterModal
				open={modalOpen}
				onClose={closeModal}
				title={
					editing
						? i18n.t("master.addons.editTitle")
						: i18n.t("master.addons.createTitle")
				}
				subtitle={i18n.t("master.addons.formHint")}
				footer={modalFooter}
				maxWidth={480}
			>
				<form id="master-addon-form" onSubmit={handleSubmit} className="space-y-1">
					<AuthField id="addon-name" label={i18n.t("master.addons.fieldName")}>
						<input
							id="addon-name"
							type="text"
							className="form-input w-full"
							required
							minLength={2}
							value={form.name}
							onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
							autoFocus
						/>
					</AuthField>
					<AuthField id="addon-quantity" label={i18n.t("master.addons.fieldQuantity")}>
						<input
							id="addon-quantity"
							type="number"
							className="form-input w-full"
							required
							min={1}
							step={1}
							value={form.quantity}
							onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
						/>
					</AuthField>
					<AuthField id="addon-price" label={i18n.t("master.addons.fieldPrice")}>
						<input
							id="addon-price"
							type="number"
							className="form-input w-full"
							required
							min={0}
							step="0.01"
							value={form.price}
							onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
						/>
					</AuthField>
					<AuthField>
						<label className="flex items-center gap-3 cursor-pointer mt-2">
							<input
								type="checkbox"
								className="w-5 h-5 rounded border-default-300 text-primary focus:ring-primary/30"
								checked={form.active}
								onChange={(e) =>
									setForm((f) => ({ ...f, active: e.target.checked }))
								}
							/>
							<span className="text-sm font-medium text-default-800 dark:text-default-200">
								{i18n.t("master.addons.fieldActive")}
							</span>
						</label>
					</AuthField>
				</form>
			</MasterModal>

			<MasterConfirmationModal
				title={
					addonToDelete
						? i18n.t("master.addons.confirmDelete", { name: addonToDelete.name })
						: i18n.t("master.addons.delete")
				}
				open={confirmDeleteOpen}
				onClose={setConfirmDeleteOpen}
				onConfirm={handleConfirmDelete}
			>
				{i18n.t("master.addons.confirmDeleteMessage")}
			</MasterConfirmationModal>
		</div>
	);
}
